/**
 * Shared hook used by every download button (chat-bottom + Monaco code blocks).
 *
 * Encapsulates the entire download flow:
 *   1. find the structured tool call associated with the message
 *   2. resolve the session UUID from the store
 *   3. POST to the secure /download-* endpoint
 *   4. save the streamed blob with file-saver
 *   5. show toast feedback
 *   6. track an in-flight `downloading` state so buttons can show a spinner
 *
 * The fallback to client-side parsing (extractTableFromMessage) lives in
 * the calling component, not here — this hook is strictly the server path.
 */
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
// Static import — was `await import("file-saver")` previously, but Monaco's
// AMD loader hijacks `window.define` and any runtime dynamic import then
// collides with "Can only have one anonymous define call per script file".
// Static imports bundle at build time and bypass the runtime loader entirely.
import { saveAs } from "file-saver";

import { createAxiosInstance } from "@/dataProvider/axios";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { DEV_AGENT_BASE_URL, devAgentAuthHeaders } from "../devAgentService";

import {
  downloadSoqlAs,
  downloadToolResultAs,
  type DownloadCorrelation,
  type ExportFormat,
} from "./downloadService";
import {
  findStructuredToolCall,
  isSoqlTool,
  type ToolCallHit,
} from "./findStructuredToolCall";

// /api/chat/export/pdf lives on the dev-agent backend (same as every other
// /api/chat/* route), not the default backend — must use DEV_AGENT_BASE_URL.
const exportClient = createAxiosInstance(DEV_AGENT_BASE_URL);

export interface UseDownloadActionResult {
  /** True if a download is currently in flight (for spinner UI). */
  downloadingFormat: ExportFormat | null;
  /**
   * Try to download THIS message's data in the given format via the secure
   * server endpoint. Returns true on success, false if the SOQL/tool wasn't
   * found or messageUuid is missing — caller can then fall back to client-side
   * parsing.
   */
  download: (format: ExportFormat) => Promise<boolean>;
}

/**
 * @param toolCalls   This message's own tool calls (NOT a global feed walk).
 * @param content     Raw assistant text — used as a SOQL/tool fallback source.
 * @param messageUuid Required for the secure endpoint to validate ownership.
 */
export function useDownloadAction(
  toolCalls: unknown[] | undefined,
  content: string,
  messageUuid: string | undefined,
): UseDownloadActionResult {
  const [downloadingFormat, setDownloadingFormat] = useState<ExportFormat | null>(null);

  const download = useCallback(
    async (format: ExportFormat): Promise<boolean> => {
      if (downloadingFormat) return false;

      const hit: ToolCallHit | null = findStructuredToolCall(toolCalls, content);
      // No structured tool call — fall back to exporting the chat content as PDF/markdown
      if (!hit) {
        if (!content || content.length < 20) {
          toast.error("No downloadable full-result dataset is attached to this message");
          return false;
        }
        setDownloadingFormat(format);
        try {
          if (format === "pdf") {
            const resp = await exportClient.post("/api/chat/export/pdf", {
              content,
              title: "OrgPilot Report",
            }, { responseType: "blob", timeout: 30000, headers: devAgentAuthHeaders() });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            saveAs(blob, "orgpilot-report.pdf");
            toast.success("Downloaded PDF report");
          } else {
            // CSV/markdown — just save the raw content
            const ext = format === "csv" ? "csv" : "md";
            const blob = new Blob([content], { type: "text/plain" });
            saveAs(blob, `orgpilot-report.${ext}`);
            toast.success(`Downloaded ${ext.toUpperCase()}`);
          }
          return true;
        } catch (err) {
          toast.error("Export failed");
          return false;
        } finally {
          setDownloadingFormat(null);
        }
      }

      const sessionUuid = useDevAgentStore.getState().sessionUuid;
      if (!sessionUuid || !messageUuid) {
        toast.error("This message is not yet correlated with the server — try refreshing");
        return false;
      }

      const corr: DownloadCorrelation = { sessionUuid, messageUuid };
      setDownloadingFormat(format);
      try {
        const result = isSoqlTool(hit.tool)
          ? await downloadSoqlAs(
              String(hit.args?.query || ""),
              format,
              corr,
              { tooling: hit.tool === "tooling_query" },
            )
          : await downloadToolResultAs(hit.tool, hit.args, format, corr);

        if (!result.blob || result.blob.size === 0) {
          toast.error("Download returned an empty file");
          return false;
        }

        saveAs(result.blob, result.filename);

        const sizeKB = (result.blob.size / 1024).toFixed(1);
        toast.success(`Downloaded ${result.filename} — ${sizeKB} KB`);
        return true;
      } catch (err: unknown) {
        const msg =
          err && typeof err === "object" && "message" in err
            ? String((err as { message?: string }).message)
            : "unknown error";
        console.warn("[useDownloadAction] server download failed:", err);
        toast.error(`Download failed — ${msg}`);
        return false;
      } finally {
        setDownloadingFormat(null);
      }
    },
    [toolCalls, content, messageUuid, downloadingFormat, exportClient],
  );

  return { downloadingFormat, download };
}
